import type { CardDef } from '../types.js';
import { apiClient } from '../api-client.js';
import { getEffectiveEntryCost, CUSTOM_PACK_TICKET_ID, type CardgenProgressEvent, type CustomPackConfig } from '../shared/cardgen.js';
import { createCardElement } from './CardView.js';
import { createShareButton } from './Share.js';

function shardPath(id: string): string {
  return `data/custom-cards/${id.slice(0, 2)}/${id.slice(2, 4)}/${id}`;
}

function variantUrl(id: string, variant: number): string {
  return `${shardPath(id)}-${String(variant).padStart(2, '0')}.png`;
}

interface SlotState {
  index: number;
  rarity?: string;
  brief?: string;
  name?: string;
  description?: string;
  status: string;
  done: boolean;
  cardId?: string;
}

type RevealCard = {
  def: CardDef;
  selectedVariant: number; // 1..4
  keep: boolean;
};

export interface CustomPackResult {
  keptCards?: CardDef[];
  cancelled?: boolean;
  failed?: string;
}

export async function createCustomPackView(
  container: HTMLElement,
  gemsAvailable: number,
  config: CustomPackConfig,
  onDone: (result: CustomPackResult) => void,
  resumeJobId?: string,
  onOpenGemShop?: () => void,
  /** Live gem balance for affordability checks on click. The static
   *  `gemsAvailable` parameter is captured at render time and may go
   *  stale after the player buys gems in the store overlay; click
   *  handlers should read through this getter instead so they always
   *  see the latest balance. Defaults to the snapshot value if the
   *  caller doesn't supply one. */
  getGems: () => number = () => gemsAvailable,
  /** Sync hook called whenever the server returns a fresh gem balance
   *  (after buy or keep). Without this the parent's playerData.gems
   *  stays at the pre-pack-purchase value, and the reveal screen's
   *  affordability check reads stale (too-high) gems — clicking
   *  Confirm with insufficient real balance would just call the
   *  server (which then 400s) instead of routing to the store. */
  onGemsChange?: (gems: number) => void,
  /** When true, this purchase will redeem a `free-custom-pack`
   *  ticket instead of charging gems. Affects only the confirm
   *  screen label + the eventual `buyCustomPack` call. The caller
   *  is responsible for verifying the player actually owns the
   *  ticket before passing true. */
  useFreeTicket: boolean = false,
): Promise<void> {
  container.innerHTML = '';
  const root = document.createElement('div');
  root.className = 'custom-pack-view';
  container.appendChild(root);

  if (resumeJobId) {
    // Skip confirm — go straight into the streaming flow for the existing job.
    await runGeneration(root, resumeJobId, config, onDone, onOpenGemShop, getGems, onGemsChange);
    return;
  }

  renderConfirm(root, gemsAvailable, config, async (hint) => {
    // User confirmed — call buy, then stream.
    renderBusy(root, 'Paying…');
    try {
      const { jobId, gems: postBuyGems } = await apiClient.buyCustomPack(
        hint,
        useFreeTicket ? CUSTOM_PACK_TICKET_ID : undefined,
      );
      // Sync the post-pack-purchase balance to the parent BEFORE the
      // reveal screen renders. Otherwise a player who spent their last
      // gems on the pack would see getGems() still reporting the
      // pre-buy balance and the Confirm button's affordability gate
      // would silently misfire.
      onGemsChange?.(postBuyGems);
      await runGeneration(root, jobId, config, onDone, onOpenGemShop, getGems, onGemsChange);
    } catch (e: any) {
      renderError(root, e?.message || 'Failed to start custom pack', () => onDone({ cancelled: true }));
    }
  }, () => onDone({ cancelled: true }), onOpenGemShop, getGems, useFreeTicket);
}

// ── Confirm modal ───────────────────────────────────────────────

function renderConfirm(
  root: HTMLElement,
  gems: number,
  config: CustomPackConfig,
  onConfirm: (hint: string) => void,
  onCancel: () => void,
  onOpenGemShop?: () => void,
  getGems: () => number = () => gems,
  useFreeTicket: boolean = false,
): void {
  root.innerHTML = '';
  const effectiveCost = getEffectiveEntryCost(config);
  const discount = config.discount;
  const panel = document.createElement('div');
  panel.className = 'custom-pack-panel' + (useFreeTicket ? ' has-ticket' : '');
  // Cost line + confirm-button label split based on whether the
  // player is redeeming a free-custom-pack ticket. The ticket path
  // shows FREE and bypasses the gem affordability gate entirely.
  const costLine = useFreeTicket
    ? `<div class="custom-pack-cost-line">Entry cost: <span class="gems">FREE</span> <span class="custom-pack-discount-tag">Ticket</span></div>`
    : `<div class="custom-pack-cost-line">Entry cost: ${discount
        ? `<span class="gems-strike">${config.entryCost}</span> <span class="gems">${effectiveCost} gems</span> <span class="custom-pack-discount-tag">${discount.label}</span>`
        : `<span class="gems">${effectiveCost} gems</span>`}</div>`;
  const confirmLabel = useFreeTicket ? 'Redeem ticket' : `Spend ${effectiveCost} gems`;
  panel.innerHTML = `
    <h1 class="custom-pack-title">Custom Pack</h1>
    <p class="custom-pack-subtitle">AI-generated cards, inscribed just for you.</p>
    ${costLine}
    <p class="custom-pack-description">
      You'll get <strong>${config.cardsPerPack}</strong> unique cards to choose from.
      Keep <strong>1</strong> for free, <strong>2</strong> for +${config.keepCosts['2']} gems,
      or all <strong>3</strong> for +${config.keepCosts['3']} gems.
    </p>
    <label class="custom-pack-hint-label">
      <span>Hint (optional)</span>
      <textarea class="custom-pack-hint-input" rows="2" maxlength="100" placeholder="e.g. Norse warriors, metal slimes, Renaissance scholars, desert nomads"></textarea>
      <span class="custom-pack-hint-sub">Tell us what you'd like to see on a card — a theme, an era, a vibe, a specific character, even just a single word. Leave blank for a random mix.</span>
    </label>
    <div class="custom-pack-actions">
      <button class="menu-btn" data-action="cancel">Cancel</button>
      <button class="menu-btn primary" data-action="confirm">${confirmLabel}</button>
    </div>
  `;
  root.appendChild(panel);
  const hintInput = panel.querySelector('.custom-pack-hint-input') as HTMLTextAreaElement;
  panel.querySelector('[data-action="cancel"]')!.addEventListener('click', onCancel);
  // Always clickable. If the player can't afford the entry cost we
  // route to the store instead of starting generation — the click
  // expresses "I want this", and either path moves them closer.
  // `getGems()` reads live so a player who buys gems in the store
  // overlay (which doesn't re-render this view) can immediately
  // confirm without a stale-affordability gate. Ticket redemption
  // skips the gate entirely.
  panel.querySelector('[data-action="confirm"]')!.addEventListener('click', () => {
    if (!useFreeTicket && getGems() < effectiveCost) onOpenGemShop?.();
    else onConfirm(hintInput.value);
  });
}

// ── Generation progress (SSE-driven) ────────────────────────────

async function runGeneration(
  root: HTMLElement,
  jobId: string,
  config: CustomPackConfig,
  onDone: (result: CustomPackResult) => void,
  onOpenGemShop?: () => void,
  getGems?: () => number,
  onGemsChange?: (gems: number) => void,
): Promise<void> {
  const slots: SlotState[] = [];
  for (let i = 1; i <= config.cardsPerPack; i++) {
    // First card shows "Thinking…" up front — the pipeline reaches its
    // candidate-name stage near-immediately, so "Waiting…" feels stale.
    // Later cards sit at "Waiting…" until their pickName turn comes.
    slots.push({ index: i, status: i === 1 ? 'Thinking…' : 'Waiting…', done: false });
  }

  renderProgress(root, slots);

  const es = new EventSource(`/api/pack/custom/${jobId}/stream`);

  es.addEventListener('progress', (ev) => {
    const event = JSON.parse((ev as MessageEvent).data) as CardgenProgressEvent;
    applyEvent(slots, event);
    renderProgress(root, slots);
  });

  es.addEventListener('ready', (ev) => {
    const { cards, gems } = JSON.parse((ev as MessageEvent).data) as { cards: CardDef[]; gems?: number };
    es.close();
    renderReveal(root, jobId, cards, config, gems ?? 0, onDone, onOpenGemShop, getGems, onGemsChange);
  });

  es.addEventListener('failed', (ev) => {
    es.close();
    const { error } = JSON.parse((ev as MessageEvent).data) as { error: string };
    renderError(root, `Generation failed: ${error}. Gems refunded.`, () => onDone({ failed: error }));
  });

  es.addEventListener('terminal', (ev) => {
    es.close();
    const { status, error } = JSON.parse((ev as MessageEvent).data) as { status: string; error?: string };
    renderError(root, `Job already ${status}${error ? `: ${error}` : ''}`, () => onDone({ failed: status }));
  });

  es.onerror = () => {
    // EventSource auto-reconnects; only give up if we're fully closed.
    if (es.readyState === EventSource.CLOSED) {
      renderError(root, 'Connection lost. Try again later.', () => onDone({ failed: 'connection lost' }));
    }
  };
}

function applyEvent(slots: SlotState[], event: CardgenProgressEvent): void {
  if (event.type === 'job_complete' || event.type === 'job_failed') return;
  const slot = slots[event.index - 1];
  if (!slot) return;
  switch (event.type) {
    case 'card_start':
      slot.rarity = event.rarity;
      slot.brief = event.brief;
      slot.status = `Choosing a ${event.rarity} card…`;
      break;
    case 'card_name':
      slot.name = event.name;
      slot.description = event.description;
      slot.status = `Writing card for ${event.name}…`;
      break;
    case 'card_stats':
      slot.status = 'Balancing stats…';
      break;
    case 'card_art':
      slot.status = 'Painting portraits…';
      break;
    case 'card_done':
      slot.status = 'Ready';
      slot.cardId = event.cardId;
      slot.done = true;
      break;
  }
}

function renderProgress(root: HTMLElement, slots: SlotState[]): void {
  root.innerHTML = '';
  const panel = document.createElement('div');
  panel.className = 'custom-pack-panel progress';
  panel.innerHTML = `
    <h1 class="custom-pack-title">Scribing your pack…</h1>
    <p class="custom-pack-subtitle">This takes a minute or two.</p>
    <div class="custom-pack-slots"></div>
  `;
  const slotsEl = panel.querySelector('.custom-pack-slots')!;
  for (const s of slots) {
    const el = document.createElement('div');
    el.className = `custom-pack-slot ${s.done ? 'done' : ''}`;
    el.dataset.rarity = s.rarity ?? '';
    el.innerHTML = `
      <div class="slot-frame">
        <div class="slot-index">#${s.index}</div>
        ${s.name ? `<div class="slot-name">${escapeHtml(s.name)}</div>` : ''}
        ${s.rarity ? `<div class="slot-rarity rarity-${s.rarity}">${s.rarity}</div>` : ''}
        ${s.description ? `<div class="slot-description">${escapeHtml(s.description)}</div>` : ''}
      </div>
      <div class="slot-status">${escapeHtml(s.status)}</div>
      ${s.brief ? `<div class="slot-theme">${escapeHtml(s.brief)}</div>` : ''}
    `;
    slotsEl.appendChild(el);
  }
  root.appendChild(panel);
}

function renderBusy(root: HTMLElement, label: string): void {
  root.innerHTML = '';
  const panel = document.createElement('div');
  panel.className = 'custom-pack-panel busy';
  panel.innerHTML = `<div class="custom-pack-spinner"></div><div class="custom-pack-busy-label">${escapeHtml(label)}</div>`;
  root.appendChild(panel);
}

function renderError(root: HTMLElement, message: string, onContinue: () => void): void {
  root.innerHTML = '';
  const panel = document.createElement('div');
  panel.className = 'custom-pack-panel error';
  panel.innerHTML = `
    <h1 class="custom-pack-title">Something went wrong</h1>
    <p class="custom-pack-subtitle">${escapeHtml(message)}</p>
    <button class="menu-btn primary" data-action="continue">Return</button>
  `;
  root.appendChild(panel);
  panel.querySelector('[data-action="continue"]')!.addEventListener('click', onContinue);
}

// ── Reveal / keep flow ──────────────────────────────────────────

interface RevealHandle {
  rc: RevealCard;
  wrapper: HTMLElement;
  toggle: HTMLButtonElement;
}

function renderReveal(
  root: HTMLElement,
  jobId: string,
  cards: CardDef[],
  config: CustomPackConfig,
  gemsAvailable: number,
  onDone: (result: CustomPackResult) => void,
  onOpenGemShop?: () => void,
  getGems: () => number = () => gemsAvailable,
  onGemsChange?: (gems: number) => void,
): void {
  const state: RevealCard[] = cards.map((def, i) => ({
    def,
    selectedVariant: 1,
    keep: i === 0, // default first card kept
  }));

  root.innerHTML = '';
  const panel = document.createElement('div');
  panel.className = 'custom-pack-panel reveal';
  panel.innerHTML = `
    <h1 class="custom-pack-title">Pick your cards</h1>
    <p class="custom-pack-subtitle">Click a card to keep it. Swap art for each keeper.</p>
    <div class="custom-pack-reveal-row"></div>
    <div class="custom-pack-footer">
      <div class="custom-pack-summary">
        <span>Keeping: <strong class="js-keep-count">0 of ${state.length}</strong></span>
        <span class="custom-pack-cost">Extra cost: <strong class="js-extra-cost">0 gems</strong></span>
        <span>Your gems: <strong class="js-gem-balance">${gemsAvailable} gems</strong>${
          onOpenGemShop ? ' <button class="menu-btn-sm gem-shop-link js-buy-gems">Buy Gems</button>' : ''
        }</span>
      </div>
      <button class="menu-btn primary js-confirm">Pick at least one</button>
    </div>
  `;
  root.appendChild(panel);

  const revealRow = panel.querySelector('.custom-pack-reveal-row') as HTMLElement;
  const keepCountEl = panel.querySelector('.js-keep-count') as HTMLElement;
  const extraCostEl = panel.querySelector('.js-extra-cost') as HTMLElement;
  const balanceEl = panel.querySelector('.js-gem-balance') as HTMLElement;
  const confirmBtn = panel.querySelector('.js-confirm') as HTMLButtonElement;
  panel.querySelector('.js-buy-gems')?.addEventListener('click', () => onOpenGemShop?.());

  const handles: RevealHandle[] = [];

  function syncKeepingAll(): void {
    for (const h of handles) {
      h.wrapper.classList.toggle('keeping', h.rc.keep);
      h.toggle.classList.toggle('keeping', h.rc.keep);
      h.toggle.textContent = h.rc.keep ? '✓ Keeping' : 'Keep';
    }
    updateFooter();
  }

  function updateFooter(): void {
    const keepCount = state.filter(s => s.keep).length;
    const cost = keepCount === 0 ? 0 : (config.keepCosts[String(keepCount)] ?? 0);
    // The displayed balance still shows the running cost against the
    // captured `gemsAvailable` — that's the player's reference point
    // when picking. The negative case is informative (they'll see
    // the deficit) but no longer disables the button: clicking with
    // not-enough-gems routes to the store instead.
    const balanceAfter = gemsAvailable - cost;
    keepCountEl.textContent = `${keepCount} of ${state.length}`;
    extraCostEl.textContent = `${cost} gems`;
    balanceEl.textContent = `${balanceAfter} gems`;
    // Only "pick at least one" is a real gate — the button has no
    // valid action with zero kept cards. Affordability is checked at
    // click time against the live balance.
    confirmBtn.disabled = keepCount === 0;
    confirmBtn.textContent =
      keepCount === 0 ? 'Pick at least one' :
      cost === 0 ? 'Confirm' : `Confirm (${cost} gems)`;
  }

  // Click-on-card logic: always ensure clicked.keep = true. If exactly one OTHER
  // card was kept before, deselect it (radio swap). Otherwise just add.
  function onCardClicked(clicked: RevealCard): void {
    if (clicked.keep) return;
    const others = state.filter(s => s.keep && s !== clicked);
    if (others.length === 1) {
      others[0].keep = false;
    }
    clicked.keep = true;
    syncKeepingAll();
  }

  function onToggleClicked(rc: RevealCard): void {
    rc.keep = !rc.keep;
    syncKeepingAll();
  }

  for (const rc of state) {
    const handle = buildRevealCard(rc, onCardClicked, onToggleClicked);
    handles.push(handle);
    revealRow.appendChild(handle.wrapper);
  }
  updateFooter();

  confirmBtn.addEventListener('click', async () => {
    const keep = state.filter(s => s.keep);
    if (keep.length === 0) return;
    // Affordability is checked at click time against the live
    // balance — `gemsAvailable` would be stale if the player just
    // bought gems in the store overlay (which patches displays in
    // place without re-rendering this view). When short, route to
    // the store; when they come back, their keep selection is still
    // intact and they can confirm immediately.
    const cost = config.keepCosts[String(keep.length)] ?? 0;
    if (getGems() < cost) {
      onOpenGemShop?.();
      return;
    }
    renderBusy(root, 'Saving your choices…');
    try {
      const imageChoices: Record<string, number> = {};
      for (const k of keep) imageChoices[k.def.id] = k.selectedVariant;
      const keepIds = keep.map(k => k.def.id);
      // Update the kept cards' `art` field client-side before we pass them up,
      // so the catalog picks up the selected variant without a re-fetch.
      const keptCards = keep.map(k => ({ ...k.def, art: `${shardPath(k.def.id)}.png` }));
      const resp = await apiClient.keepCustomCards(jobId, keepIds, imageChoices);
      onGemsChange?.(resp.gems);
      onDone({ keptCards });
    } catch (e: any) {
      renderError(root, e?.message || 'Failed to save choices', () => onDone({ cancelled: true }));
    }
  });
}

function buildRevealCard(
  rc: RevealCard,
  onCardClick: (rc: RevealCard) => void,
  onToggleClick: (rc: RevealCard) => void,
): RevealHandle {
  const wrapper = document.createElement('div');
  wrapper.className = `custom-pack-reveal-card ${rc.keep ? 'keeping' : ''}`;

  // Stable preview card. We only ever update its art <img>, never re-render.
  // Tooltip is suppressed — the .details panel below already shows the full
  // ability / tags / flavor, and the hover popup gets in the way of mobile
  // scrolling and overlaps the card itself on smaller screens.
  const defWithArt: CardDef = { ...rc.def, art: variantUrl(rc.def.id, rc.selectedVariant) };
  const cardEl = createCardElement(defWithArt, undefined, false, undefined, false, 0, false, true);
  cardEl.classList.add('reveal-card-preview');
  cardEl.addEventListener('click', () => onCardClick(rc));
  wrapper.appendChild(cardEl);
  const cardArtImg = cardEl.querySelector('.card-art img') as HTMLImageElement | null;

  // Details panel — full ability/tags/flavor so players don't need to hover.
  wrapper.appendChild(buildDetailsPanel(rc.def));

  // Filmstrip — only visible when keeping (via CSS).
  const filmstrip = document.createElement('div');
  filmstrip.className = 'custom-pack-filmstrip';
  const thumbs: HTMLButtonElement[] = [];
  for (let v = 1; v <= 4; v++) {
    const thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = `filmstrip-thumb ${rc.selectedVariant === v ? 'selected' : ''}`;
    thumb.innerHTML = `<img src="${variantUrl(rc.def.id, v)}" alt="variant ${v}" loading="lazy" />`;
    const captured = v;
    thumb.addEventListener('click', () => {
      if (rc.selectedVariant === captured) return;
      rc.selectedVariant = captured;
      const url = variantUrl(rc.def.id, captured);
      if (cardArtImg) cardArtImg.src = url;
      // The tooltip closure in createCardElement captured this def by reference;
      // mutating `art` here means the next tooltip render picks up the new image.
      defWithArt.art = url;
      for (const t of thumbs) t.classList.remove('selected');
      thumb.classList.add('selected');
    });
    filmstrip.appendChild(thumb);
    thumbs.push(thumb);
  }
  wrapper.appendChild(filmstrip);

  const actions = document.createElement('div');
  actions.className = 'custom-pack-reveal-actions';

  const shareBtn = createShareButton(rc.def.id, { getVariant: () => rc.selectedVariant });
  actions.appendChild(shareBtn);

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = `custom-pack-keep-toggle ${rc.keep ? 'keeping' : ''}`;
  toggle.textContent = rc.keep ? '✓ Keeping' : 'Keep';
  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    onToggleClick(rc);
  });
  actions.appendChild(toggle);

  wrapper.appendChild(actions);

  return { rc, wrapper, toggle };
}

function buildDetailsPanel(def: CardDef): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'reveal-card-details';

  const rarityRow = document.createElement('div');
  rarityRow.className = `details-rarity rarity-${def.rarity}`;
  rarityRow.textContent = def.rarity;
  panel.appendChild(rarityRow);

  // Ability is already rendered on the card itself; don't repeat it here.

  // Tags
  if (def.tags && def.tags.length > 0) {
    const tags = document.createElement('div');
    tags.className = 'details-tags';
    for (const t of def.tags) {
      const chip = document.createElement('span');
      chip.className = 'details-tag';
      chip.textContent = t;
      tags.appendChild(chip);
    }
    panel.appendChild(tags);
  }

  // Flavor
  if (def.flavor) {
    const flavor = document.createElement('div');
    flavor.className = 'details-flavor';
    flavor.textContent = def.flavor;
    panel.appendChild(flavor);
  }

  return panel;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
